import { memo, useEffect, useRef } from 'react';
import { StaticImageData } from 'next/image';
import classnames from 'classnames';
import gsap from 'gsap';

import styles from './WakandaCourses.module.scss';

import BaseImage from '@/components/BaseImage/BaseImage';
import Cta from '@/components/Cta/Cta';
import ShareLinks from '@/components/ShareLinks/ShareLinks';
import VideoContainer from '@/components/VideoContainer/VideoContainer';

import { shareCopy } from '@/data/data';

export type Props = {
  className?: string;
  eyebrow: string;
  title: string;
  logo: {
    data: StaticImageData;
    alt: string;
  };
  bgImage: StaticImageData;
  videos: {
    id: number;
    src: string;
    share: {
      title: string;
      description: string;
      src: string;
      fileName: string;
    };
    poster: { data: StaticImageData; alt?: string };
    heading: string;
    description: string;
    cta: {
      href: string;
      title: string;
    };
  }[];
  share: string;
  patterns: {
    top: StaticImageData;
    middle: StaticImageData;
    bottom: StaticImageData;
  };
};

function WakandaCourses({ className, title, bgImage, eyebrow, videos, patterns }: Props) {
  const bgRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    gsap.to(bgRef.current, { duration: 8, repeat: -1, '--gradient-angle': '-360deg' });
  });

  return (
    <section className={classnames(styles.WakandaCourses, className)}>
      <div className={styles.bg} ref={bgRef} />
      <BaseImage className={styles.patternTop} data={patterns.top} />
      <div className={styles.wrapper}>
        <p className={styles.eyebrow}>{eyebrow}</p>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>{title}</h2>
        </div>
      </div>
      {videos.map(({ id, src, share, poster, heading, description, cta }, index) => (
        <div className={classnames(styles.item)} key={index}>
          <div className={classnames(styles.videoWrapper)}>
            <VideoContainer className={styles.video} src={src} poster={poster} bgImage={bgImage} isOdd={index === 0} />
            <BaseImage className={styles.pattern} data={index === 0 ? patterns.middle : patterns.bottom} />
            {index !== 0 && <div className={styles.square} />}
          </div>
          <div className={styles.lowerWrapper}>
            <div className={styles.number}>{id < 10 ? '0' + id : id}</div>
            <h3 className={styles.heading}>{heading}</h3>
            <p className={styles.description}>{description}</p>
            <div className={styles.links}>
              <Cta {...cta} />
              <ShareLinks title={shareCopy.share} share={share} href={cta.href} />
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}

export default memo(WakandaCourses);
